import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/api/client";
import { Skeleton } from "@/components/Skeleton";
import { FeedRow } from "@/components/audit/FeedRow";
import { kindLabel, type FeedItem, type FeedResponse } from "@/lib/auditKinds";

const PAGE = 50;

interface ActorRef {
  id: string | null;
  name: string | null;
  role: string | null;
}

/** Group a flat, time-ordered feed into Tbilisi calendar days. */
function byDay(items: FeedItem[]): [string, FeedItem[]][] {
  const out: [string, FeedItem[]][] = [];
  for (const it of items) {
    const key = new Date(it.at).toLocaleDateString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
    const last = out[out.length - 1];
    if (last && last[0] === key) last[1].push(it);
    else out.push([key, [it]]);
  }
  return out;
}

export function WorkTab({
  range,
  actors,
  pulse = 0,
}: {
  range: string;
  actors: ActorRef[];
  /** Bumped when a live activity event arrives. Refreshes the first page in
   *  place — remounting would throw away the reader's scroll position and any
   *  pages they had already loaded. */
  pulse?: number;
}) {
  const [items, setItems] = useState<FeedItem[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actor, setActor] = useState("");
  const [kinds, setKinds] = useState<string[]>([]);
  const [vocab, setVocab] = useState<string[]>([]);
  const seq = useRef(0);

  const qs = useCallback(
    (offset: number) => {
      const p = new URLSearchParams({ range, limit: String(PAGE), offset: String(offset) });
      if (actor) p.set("actor_user_id", actor);
      kinds.forEach((k) => p.append("kind", k));
      return p.toString();
    },
    [range, actor, kinds],
  );

  // Reset and reload whenever the window or filters change.
  useEffect(() => {
    const s = ++seq.current;
    setLoading(true);
    api<FeedResponse>(`/api/audit/feed?${qs(0)}`)
      .then((r) => {
        if (s !== seq.current) return;
        setItems(r.items);
        setHasMore(r.has_more);
        if (r.kinds?.length) setVocab(r.kinds);
      })
      .catch(() => {
        if (s === seq.current) setItems([]);
      })
      .finally(() => {
        if (s === seq.current) setLoading(false);
      });
  }, [qs]);

  const loadMore = useCallback(async () => {
    const s = seq.current;
    const r = await api<FeedResponse>(`/api/audit/feed?${qs(items.length)}`);
    if (s !== seq.current) return;
    setItems((prev) => [...prev, ...r.items]);
    setHasMore(r.has_more);
  }, [qs, items.length]);

  const paged = items.length > PAGE;
  useEffect(() => {
    if (!pulse || paged) return;
    const s = ++seq.current;
    api<FeedResponse>(`/api/audit/feed?${qs(0)}`)
      .then((r) => {
        if (s === seq.current) {
          setItems(r.items);
          setHasMore(r.has_more);
        }
      })
      .catch(() => {});
    // `qs`/`paged` are read but must not retrigger — only a new pulse should.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pulse]);

  const days = useMemo(() => byDay(items), [items]);

  const toggleKind = (k: string) =>
    setKinds((prev) => (prev.includes(k) ? prev.filter((x) => x !== k) : [...prev, k]));

  return (
    <div className="space-y-4">
      {/* filters */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={actor}
          onChange={(e) => setActor(e.target.value)}
          className="rounded-lg bg-ink-900 border border-white/10 text-sm text-slate-200 px-3 py-2"
        >
          <option value="">Everyone</option>
          {actors.map((a) => (
            <option key={a.id ?? ""} value={a.id ?? ""}>
              {a.name}
            </option>
          ))}
        </select>
        <div className="flex flex-wrap gap-1.5">
          {vocab.map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => toggleKind(k)}
              className={`rounded-md px-2 py-1 text-[11.5px] border transition ${
                kinds.includes(k)
                  ? "bg-accent-500/15 text-accent-200 border-accent-500/30"
                  : "text-slate-500 hover:text-slate-300 border-white/5"
              }`}
            >
              {kindLabel(k)}
            </button>
          ))}
          {kinds.length > 0 && (
            <button
              type="button"
              onClick={() => setKinds([])}
              className="rounded-md px-2 py-1 text-[11.5px] text-slate-500 hover:text-slate-300 border border-white/5"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="card p-5 space-y-3">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="card p-10 text-center text-sm text-slate-500">
          Nothing happened in this window.
        </div>
      ) : (
        <div className="space-y-4">
          {days.map(([day, rows]) => (
            <section key={day} className="card overflow-hidden">
              <div className="px-5 py-2.5 border-b border-white/5 flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  {day}
                </span>
                <span className="text-[11px] text-slate-600 tabular-nums">
                  {rows.length}
                </span>
              </div>
              <div className="px-5">
                {rows.map((it) => (
                  <FeedRow key={it.id} item={it} />
                ))}
              </div>
            </section>
          ))}
          {hasMore && (
            <button
              type="button"
              onClick={loadMore}
              className="w-full rounded-lg border border-white/10 py-2 text-sm text-slate-400 hover:bg-white/5 transition"
            >
              Load more
            </button>
          )}
        </div>
      )}
    </div>
  );
}
